import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type } from "arktype";
import { Loader2, Pencil } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";

import { CustomFieldValues } from "@/api/document/custom-field-values";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { OverlayShell } from "@/components/ui/overlay";
import { SkeletonRows } from "@/components/ui/skeleton";
import { DocumentFieldLabels } from "@/lib/labels/document-fields";

import { SectionCard } from "./section-card";

const formSchema = type({ value: type("string.trim").to("string > 0") });

interface PolicyDraft {
	key: string;
	label: string;
	value: string;
	inputType: Parameters<typeof CustomFieldValues.mask>[0];
}

export function DocumentPoliciesSection() {
	const { data } = useQuery(orpc.documents.policies.list.queryOptions());
	const [editing, setEditing] = useState<PolicyDraft | null>(null);

	return (
		<SectionCard
			title={DocumentFieldLabels.policies.title}
			dataComponent="system-document-policies"
		>
			<p className="mb-2 text-xs text-muted-foreground">
				{DocumentFieldLabels.policies.description}
			</p>

			{data === undefined && <SkeletonRows rows={4} />}

			{data !== undefined && (
				<ul className="flex flex-col divide-y divide-border">
					{data.map((policy) => (
						<li key={policy.id} className="flex items-center gap-3 py-2">
							<div className="min-w-0 flex-1">
								<p className="text-xs text-muted-foreground">{policy.label}</p>
								<p className="text-sm font-medium text-foreground">
									{CustomFieldValues.format(policy.input_type, policy.value)}
								</p>
								<p className="text-xs text-muted-foreground">
									{DocumentFieldLabels.policies.version(policy.version)}
								</p>
							</div>

							<Button
								size="icon"
								variant="ghost"
								className="size-8"
								aria-label={DocumentFieldLabels.policies.edit(policy.label)}
								onClick={() =>
									setEditing({
										key: policy.key,
										label: policy.label,
										value: CustomFieldValues.format(policy.input_type, policy.value),
										inputType: policy.input_type,
									})
								}
							>
								<Pencil />
							</Button>
						</li>
					))}
				</ul>
			)}

			{editing !== null && (
				<DocumentPolicyEditModal policy={editing} onClose={() => setEditing(null)} />
			)}
		</SectionCard>
	);
}

function DocumentPolicyEditModal({ policy, onClose }: { policy: PolicyDraft; onClose(): void }) {
	const queryClient = useQueryClient();
	const {
		control,
		handleSubmit,
		formState: { isDirty, isValid },
	} = useForm<typeof formSchema.infer>({
		resolver: arktypeResolver(formSchema),
		mode: "onChange",
		defaultValues: { value: policy.value },
	});
	const { mutate, isPending } = useMutation(
		orpc.documents.policies.update.mutationOptions({
			onSuccess: () => {
				void queryClient.invalidateQueries({ queryKey: orpc.documents.policies.list.key() });
				toast.success(DocumentFieldLabels.policies.saved);
				onClose();
			},
			onError: () => toast.error(DocumentFieldLabels.policies.error),
		}),
	);
	const submit = handleSubmit((form) => mutate({ key: policy.key, value: form.value }));

	return (
		<OverlayShell
			label={policy.label}
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<form onSubmit={submit} className="overlay-panel max-w-md gap-4 p-5">
				<div>
					<h2 className="type-section-title text-foreground">{policy.label}</h2>
					<p className="text-xs text-muted-foreground">{DocumentFieldLabels.policies.newVersion}</p>
				</div>

				<Controller
					control={control}
					name="value"
					render={({ field }) => (
						<FormField
							label={DocumentFieldLabels.policies.value}
							value={field.value}
							onChange={(value) => field.onChange(CustomFieldValues.mask(policy.inputType, value))}
							autoFocus
						/>
					)}
				/>

				<div className="flex justify-end gap-2">
					<Button type="button" variant="secondary" size="sm" onClick={onClose}>
						{DocumentFieldLabels.cancel}
					</Button>

					<Button
						variant="secondary"
						size="sm"
						type="submit"
						disabled={!isDirty || !isValid || isPending}
					>
						{isPending && <Loader2 className="size-4 animate-spin" />}
						{DocumentFieldLabels.save}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
